import React from 'react';
import PropTypes from 'prop-types';
import { WeezHeader, Button, Card, Message, ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import useAppContext from '../../../../context/useAppContext';
import ProductModel from '../../../../models/productModel';
import RenderFormProduct from './components/renderFormProduct';
import VariantModel from '../../../../models/variantModel';
import { convertMenhirToPyvarPipeTranslate } from '../../../../components/utils/pipeTranslateConvert';
import { useForm, requiredField } from '@weezevent/weezjs-core';
import { getProductsLanguages, getDefaultLanguage, removeDefaultLanguage } from '../../utils/translationUtils';

export function WarningArchive({ forceProduct = null }) {
    const { t } = useTranslation();
    const [context] = useAppContext();
    const { organization, product, reloadProduct } = context;

    const onReactivate = React.useCallback(() => {
        const productToUse = forceProduct || product;
        productToUse.archived_at = null;
        productToUse.save({ organizationId: organization.id }).then(() => {
            reloadProduct();
        });
    }, [forceProduct, product, reloadProduct]);

    const isArchived = React.useMemo(() => {
        if (!forceProduct && !product?.archived_at) {
            return false;
        }

        if (forceProduct && !forceProduct?.archived_at) {
            return false;
        }
        return true;
    }, [forceProduct, product]);

    if (!isArchived) {
        return null;
    }

    return (
        <Message
            title={t('ticket.cross-selling.warningArchived.title')}
            type="danger"
            links={[{ label: t('ticket.cross-selling.warningArchived.action'), onClick: () => onReactivate() }]}
        >
            {t('ticket.cross-selling.warningArchived.subtitle')}
        </Message>
    );
}

export default function FormProduct({ organization, productId }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    // We’re not supposed to exceed the maximum number of characters. But we can if the description is done in the old BO
    // and in that case you can't modify the product in the NBO, this error is an indication to dodge run
    const validateDescription = React.useCallback((key, description) => {
        return description.length > 450 ? 'ticket.cross-selling.add.too_many_characters_description' : true;
    }, []);

    const [productForm, setProductForm, setProductFormObject, productFormErrors, productFormValidate, setProductFormErrors] = useForm(
        {
            id: null,
            organization: organization.id,
            name: '',
            description: '',
            disabled: false,
            for_buyer: false,
            choose_none_option: false,
            display_quantity: false,
            display_out_of_stock: false,
            display_order: 0,
            category: 2,
            tva: null,
            translations: {
                name: {},
                description: {}
            }
        },
        {
            name: requiredField,
            tva: requiredField,
            description: validateDescription
        }
    );

    const productsLanguages = React.useMemo(() => getProductsLanguages(organization), [organization]);
    const defaultLanguage = React.useMemo(() => getDefaultLanguage(organization, productsLanguages), [organization, productsLanguages]);

    const [submitting, setSubmitting] = React.useState(false);

    const getPipeTranslate = React.useCallback(
        string => {
            let translations = {};

            if (string.includes('|')) {
                string.split('|').forEach(item => {
                    let pipe = convertMenhirToPyvarPipeTranslate(item);
                    let regLang = pipe.slice(1, 6).toLowerCase();
                    pipe = pipe.slice(7, pipe.length);
                    if (productsLanguages?.find(l => regLang === l)) translations[regLang] = pipe;
                });
            }
            return translations;
        },
        [productsLanguages]
    );

    const initTranslations = React.useCallback(
        (obj, lang, fields) => {
            if (!obj.translations) obj.translations = {};
            fields?.forEach(field => {
                const pipeTranslate = getPipeTranslate(obj[field]);
                obj.translations[field] = { ...pipeTranslate, ...obj.translations[field] };
                //use name if no translations for default language
                if (obj.translations[field][lang]) obj[field] = obj.translations[field][lang];
                if (!obj.translations[field][lang]) obj.translations[field][lang] = obj[field];
                //we filter the languages keys that arent in organization
                Object.keys(obj.translations[field]).forEach(langKey => {
                    if (!productsLanguages.some(ol => ol === langKey)) obj.translations[field][langKey] = '';
                });
            });
            return obj;
        },
        [productsLanguages]
    );

    const [product, loadingProduct, sync] = ProductModel.useApiModel(
        {
            organizationId: organization.id,
            id: productId,
            launch: Boolean(productId),
            cache: false,
            allow_cache: false,
            onSuccess: response => {
                let obj = response.object;
                //we init the translations in case we don't have any
                obj = initTranslations(obj, defaultLanguage, ['name', 'description']);
                setProductFormObject(
                    {
                        id: obj.id,
                        organization: organization.id,
                        name: obj.name,
                        description: obj.description,
                        disabled: obj.disabled,
                        for_buyer: obj.for_buyer,
                        choose_none_option: obj.choose_none_option,
                        display_quantity: obj.display_quantity,
                        display_out_of_stock: obj.display_out_of_stock,
                        display_order: obj.display_order,
                        category: obj.category,
                        tva: obj.tva,
                        translations: obj.translations
                    },
                    true
                );
            }
        },
        [productId, organization, defaultLanguage]
    );

    const [variants, loaderVariant] = VariantModel.useApiModel(
        {
            organizationId: organization.id,
            productId: productId,
            launch: Boolean(productId),
            query: {
                include: ['transaction_total_count']
            }
        },
        [productId, organization, defaultLanguage]
    );

    const onSave = React.useCallback(() => {
        if (!productFormValidate()) {
            return toast.error(t('ticket.cross-selling.add.form_error'));
        }
        const payload = {
            ...productForm,
            translations: removeDefaultLanguage(productForm.translations, defaultLanguage)
        };
        setSubmitting(true);
        return new ProductModel({ id: payload.id, ...payload })
            .save({
                organizationId: organization.id
            })
            .then(() => {
                toast.success(t('ticket.cross-selling.add.productEdited'));
            })
            .catch(e => {
                toast.error(t(`ticket.cross-selling.add.productEditError`));
            })
            .finally(() => {
                setSubmitting(false);
                sync();
            });
    }, [organization, sync, defaultLanguage]);

    const productIsArchived = React.useMemo(() => product?.archived_at != null, [product]);

    return (
        <>
            <WeezHeader
                title={t('ticket.cross-selling.add.settingsTitle')}
                rightComponent={[
                    <Button
                        primary
                        key="rate"
                        disabled={productIsArchived}
                        isLoading={submitting}
                        onClick={() => {
                            onSave();
                        }}
                        label={t('ticket.common.action.save')}
                    />
                ]}
            >
                {t('ticket.cross-selling.add.settingsSubtitle')}
            </WeezHeader>
            <WarningArchive forceProduct={product} />
            <Card>
                <Card.Header>
                    <Card.Title title={t('ticket.cross-selling.add.productGeneralInfo')} />
                </Card.Header>
                <RenderFormProduct
                    form={productForm}
                    disabled={productIsArchived}
                    setForm={setProductForm}
                    setFormErrors={setProductFormErrors}
                    setFormObject={setProductFormObject}
                    formErrors={productFormErrors}
                    formValidate={productFormValidate}
                    variants={variants}
                    transaction_total_count={product?.transaction_total_count}
                    defaultLanguage={defaultLanguage}
                    isLoading={Boolean(productId && loadingProduct) || submitting || loaderVariant}
                />
            </Card>
        </>
    );
}

FormProduct.propTypes = {
    organization: PropTypes.object.isRequired,
    productId: PropTypes.string.isRequired
};
